import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  App,
  Badge,
  Button,
  Dropdown,
  Flex,
  Input,
  Layout,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { MoreOutlined, PlusOutlined } from "@ant-design/icons";
import {
  BotBillableStatus,
  BotInterface,
  BotStatus,
  useArchiveBotMutation,
  useGetBotsQuery,
  usePauseBotMutation,
  useResumeBotMutation,
} from "api";
import { useFilters } from "hooks";
import { format, getRouteByName, palette } from "libs";
import { useAppSelector } from "store/hooks";
import { Put, getErrorMessage } from "components";
import { AddBotModal } from "./BotPage/components/AddBotModal";

interface BotsFiltersInterface {
  page: number;
  per_page?: number;
  search?: string;
  status?: BotStatus;
  billable_status?: BotBillableStatus;
}

const STATUS_LABELS: Record<BotStatus, { label: string; color: string }> = {
  [BotStatus.ACTIVE]: { label: "Activ", color: "green" },
  [BotStatus.PAUSED]: { label: "Pauzat", color: "orange" },
  [BotStatus.ARCHIVED]: { label: "Arhivat", color: "default" },
};

const BILLABLE_LABELS: Record<
  BotBillableStatus,
  { label: string; color: string }
> = {
  [BotBillableStatus.ACTIVE]: { label: "Platit", color: "green" },
  [BotBillableStatus.GRACE]: { label: "Grace", color: "gold" },
  [BotBillableStatus.DISABLED_UNPAID]: { label: "Neplatit", color: "red" },
  [BotBillableStatus.EXPIRED]: { label: "Expirat", color: "default" },
};

export const BotsPage = () => {
  const navigate = useNavigate();
  const { notification, modal } = App.useApp();

  const { isAll, isBotsRead } = useAppSelector(
    (state) => state.loggedAdmin.parsedPermissions
  );

  const { filters, onFiltersChange } = useFilters<BotsFiltersInterface>(
    {
      page: 1,
      per_page: 15,
      search: undefined,
      status: undefined,
      billable_status: undefined,
    },
    { keyword: "bots" }
  );

  const { data, isLoading, isFetching, refetch } = useGetBotsQuery(filters);

  const [archiveBot, { isLoading: isArchiving }] = useArchiveBotMutation();
  const [pauseBot] = usePauseBotMutation();
  const [resumeBot] = useResumeBotMutation();

  const [addModalOpen, setAddModalOpen] = useState(false);

  useEffect(() => {
    if (!isAll && !isBotsRead) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [isAll, isBotsRead, navigate]);

  const operationalCount = useMemo(
    () =>
      data?.data?.filter((b) => b.is_operational).length ??
      data?.pagination?.total ??
      0,
    [data]
  );

  const onArchive = async (bot: BotInterface) => {
    modal.confirm({
      title: `Arhiveaza botul "${bot.name}"?`,
      content:
        "Botul nu va mai actiona dar ramane platit pana la finalul ciclului curent. Banii nu se returneaza. Decizia este IREVERSIBILA.",
      okText: "Arhiveaza",
      okButtonProps: { danger: true, loading: isArchiving },
      cancelText: "Anuleaza",
      onOk: async () => {
        try {
          await archiveBot({ id: bot.id }).unwrap();
          notification.success({ message: "Bot arhivat." });
          refetch();
        } catch (e) {
          notification.error({ message: getErrorMessage(e as never) });
        }
      },
    });
  };

  const onTogglePause = async (bot: BotInterface) => {
    try {
      if (bot.status.value === BotStatus.PAUSED) {
        await resumeBot({ id: bot.id }).unwrap();
        notification.success({ message: "Bot reluat." });
      } else {
        await pauseBot({ id: bot.id }).unwrap();
        notification.success({ message: "Bot pauzat." });
      }
      refetch();
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
    }
  };

  const columns = useMemo<ColumnsType<BotInterface>>(
    () => [
      {
        title: "Nume",
        dataIndex: "name",
        render: (name, bot) => (
          <Link
            to={getRouteByName("BotPage", { botId: bot.id })}
            style={{ color: palette.link }}
          >
            {name}
          </Link>
        ),
      },
      {
        title: "Persona",
        render: (_, bot) =>
          bot.persona
            ? `${bot.persona.display_first_name} ${bot.persona.display_last_name}`
            : "—",
      },
      {
        title: "Status",
        dataIndex: ["status", "value"],
        render: (value: BotStatus) => {
          const cfg = STATUS_LABELS[value];
          return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : "—";
        },
      },
      {
        title: "Plata",
        dataIndex: ["billable_status", "value"],
        render: (value: BotBillableStatus) => {
          const cfg = BILLABLE_LABELS[value];
          return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : "—";
        },
      },
      {
        title: "Op.",
        dataIndex: "is_operational",
        align: "center" as const,
        width: 60,
        render: (op: boolean) => (
          <Badge status={op ? "success" : "default"} />
        ),
      },
      {
        title: "Categorii",
        dataIndex: "categories",
        render: (cats: BotInterface["categories"]) => (
          <Put condition={cats.length > 0}>
            <Space wrap size={[4, 4]}>
              {cats.map((c) => (
                <Tag key={c.id}>{c.name}</Tag>
              ))}
            </Space>
          </Put>
        ),
      },
      {
        title: "Tasks",
        dataIndex: "task_count",
        align: "right" as const,
        width: 80,
        render: (count?: number) => count ?? 0,
      },
      {
        title: "Creat",
        dataIndex: "created_at",
        render: (createdAt: string | null) =>
          createdAt ? format(createdAt) : "—",
      },
      {
        title: "Actiuni",
        align: "right" as const,
        width: 60,
        render: (_, bot) => (
          <Dropdown
            menu={{
              items: [
                {
                  key: "view",
                  label: "Deschide",
                  onClick: () =>
                    navigate(getRouteByName("BotPage", { botId: bot.id })),
                },
                bot.status.value !== BotStatus.ARCHIVED
                  ? {
                      key: "toggle-pause",
                      label:
                        bot.status.value === BotStatus.PAUSED
                          ? "Reia"
                          : "Pauzeaza",
                      onClick: () => onTogglePause(bot),
                    }
                  : null,
                bot.status.value !== BotStatus.ARCHIVED
                  ? {
                      type: "divider" as const,
                    }
                  : null,
                bot.status.value !== BotStatus.ARCHIVED
                  ? {
                      key: "archive",
                      label: "Arhiveaza",
                      danger: true,
                      onClick: () => onArchive(bot),
                    }
                  : null,
              ].filter(Boolean) as never,
            }}
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  return (
    <>
      <Flex justify="space-between" style={{ marginBottom: "1rem" }}>
        <Space wrap>
          <Input.Search
            placeholder="Cauta dupa nume"
            value={filters.search}
            allowClear
            onChange={(e) =>
              onFiltersChange({ search: e.target.value, page: 1 })
            }
          />
          <Select
            placeholder="Status"
            style={{ width: 140 }}
            allowClear
            value={filters.status}
            onChange={(val) => onFiltersChange({ status: val, page: 1 })}
            options={Object.entries(STATUS_LABELS).map(([value, cfg]) => ({
              value: Number(value),
              label: cfg.label,
            }))}
          />
          <Select
            placeholder="Plata"
            style={{ width: 140 }}
            allowClear
            value={filters.billable_status}
            onChange={(val) =>
              onFiltersChange({ billable_status: val, page: 1 })
            }
            options={Object.entries(BILLABLE_LABELS).map(([value, cfg]) => ({
              value: Number(value),
              label: cfg.label,
            }))}
          />
        </Space>
        <Space>
          <Typography.Text type="secondary">
            Operationali: <strong>{operationalCount}</strong>
          </Typography.Text>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddModalOpen(true)}
          >
            Adauga bot
          </Button>
        </Space>
      </Flex>

      <Layout style={{ backgroundColor: palette.white }}>
        <Table<BotInterface>
          columns={columns}
          dataSource={data?.data ?? []}
          loading={isLoading || isFetching}
          rowKey="id"
          scroll={{ x: 1100 }}
          pagination={{
            total: data?.pagination?.total ?? 0,
            current: data?.pagination?.currentPage ?? 1,
            pageSize: data?.pagination?.itemsPerPage ?? 15,
            onChange: (page) => onFiltersChange({ page }),
            showSizeChanger: false,
          }}
        />
      </Layout>

      <AddBotModal
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onCreated={() => refetch()}
        currentBotCount={operationalCount}
      />
    </>
  );
};
